import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { HelpCircle } from 'lucide-react';
import { openZendeskChat } from '../utils/chat';

const FAQSection = () => {
  const faqs = [
    {
      question: 'What exactly does a trademark protect?',
      answer: 'A trademark protects names, logos, slogans, and other identifiers that distinguish your products or services from competitors. Once registered, you gain exclusive nationwide rights to use that mark in your industry, the ability to sue infringers in federal court, and a legal presumption of ownership that strengthens over time.'
    },
    {
      question: 'How quickly can you file my application?',
      answer: 'We prepare and file your trademark application within 24 hours of receiving your completed intake form. After filing, the USPTO review process typically takes 8-12 months. We monitor your application throughout and handle any office actions or examiner questions that come up during review.'
    },
    {
      question: 'What comes with each service package?',
      answer: 'Every package includes a trademark availability search, professional application preparation, electronic filing with the USPTO, real-time status tracking, and direct support from your assigned advisor. Higher tiers add comprehensive clearance reports, attorney-led review, cease-and-desist drafting, priority filing, and extended monitoring.'
    },
    {
      question: 'How much should I budget for trademark registration?',
      answer: 'Our professional service fees start at $45 for the Starter package. In addition, the USPTO charges a government filing fee of $250-$350 per class of goods or services, which is paid directly to them. We offer four tiers so you can choose the level of support that matches your needs and risk tolerance.'
    },
    {
      question: 'What happens if the USPTO rejects my application?',
      answer: 'If the examiner issues an office action, we notify you immediately with a clear explanation of the issue and recommended response strategy. Our Professional and Enterprise packages include full support for drafting and submitting office action responses. Most rejections can be resolved through proper argumentation and minor amendments.'
    },
    {
      question: 'Should I register my business name, logo, or both?',
      answer: 'It depends on your brand strategy. A word mark protects your name regardless of how it appears visually. A design mark protects your specific logo. Many businesses file both for maximum coverage. During your intake, our team will recommend the strongest filing strategy based on your brand assets and competitive landscape.'
    },
    {
      question: 'I already have an LLC. Do I still need a trademark?',
      answer: 'Yes. An LLC or business registration only gives you the right to operate under that name in your state. A federal trademark gives you exclusive nationwide rights to use your brand in commerce. Without one, another company could legally adopt a similar name in a different state and compete directly against you.'
    },
    {
      question: 'What is the difference between a trademark and a copyright?',
      answer: 'Trademarks protect brand identifiers like names, logos, and taglines used in commerce. Copyrights protect original creative works such as books, music, software, and visual art. Trademarks can last indefinitely with proper renewal, while copyrights typically last the creator\'s lifetime plus 70 years. Most businesses benefit from both.'
    },
    {
      question: 'Can I file a trademark on my own without help?',
      answer: 'Technically yes, but the risk is significant. Improperly classified applications, missed conflicts, and formatting errors are the top reasons for rejection and delay. Our team conducts proper clearance searches, selects the right Nice classifications, and prepares applications that meet USPTO standards — giving you a much higher chance of approval.'
    },
    {
      question: 'What is Amazon Brand Registry and why does it matter?',
      answer: 'Amazon Brand Registry is a program that gives brand owners powerful tools to control their product listings, access A+ Content and Stores, report counterfeit products, and view brand analytics. Enrollment requires a registered or pending trademark. If you sell on Amazon, Brand Registry is essential for protecting your revenue and brand reputation.'
    }
  ];

  return (
    <section className="md:py-20 py-5 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-gradient rounded-full mb-4">
              <HelpCircle className="text-white" size={32} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
              Frequently Asked{' '}
              <span className="bg-brand-gradient bg-clip-text text-transparent">
                Questions
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Straight answers to the questions we hear most from founders and business owners
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="bg-navy-700 rounded-2xl shadow-xl p-8">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-navy-600 last:border-0">
                  <AccordionTrigger className="text-left hover:text-brand-primary transition-colors py-5">
                    <span className="font-semibold text-lg text-gray-100 pr-4">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-gray-300 leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-lg text-gray-300 mb-4">
              Don't see your question? Our advisors are ready to help.
            </p>
            
            <a
              href="#"
              onClick={(event) => {
                event.preventDefault();
                openZendeskChat();
              }}
              data-no-hover="true"
              className="js-chat inline-flex items-center text-brand-primary hover:text-gold-400 font-semibold text-lg group"
            >
              Contact our support team
              <svg
                className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
